import { NexoyaPortfolioParentContentsSortField, NexoyaSortOrder } from '../types';
import { useProviderFilterStore, type ProviderFilterState } from './provider-filter';
import { useContentRuleFilterStore, type ContentRuleFilterState } from './content-rule-filter';
import { useTaxonomyFilterStore, type TaxonomyFilterState } from './taxonomy-filter';
import { useContentLabelFilterStore, type ContentLabelFilterState } from './content-label-filter';
import { create } from 'zustand';

export type { AdAccountFilterItem } from './provider-filter';

const DEFAULT_PAGE_SIZE = 50;

// Shared / cross-cutting state that doesn't belong to any single filter slice
export interface SharedFilterState {
  titleContains?: string;
  isIncludedInOptimization?: boolean;
  isRuleManaged?: boolean;

  sortField?: NexoyaPortfolioParentContentsSortField;
  sortOrder?: NexoyaSortOrder;

  portfolioId?: number;
  trackPortfolioChange: (id: number) => boolean;

  handleChangeTitleContains: (title: string) => void;
  handleSetIsIncludedInOptimization: (value: boolean) => void;
  handleSetIsRuleManaged: (value: boolean) => void;
  handleChangeSortField: (field: NexoyaPortfolioParentContentsSortField) => void;
  handleChangeSortOrder: (order: NexoyaSortOrder) => void;

  pageSize: number;
  setPageSize: (pageSize: number) => void;
}

export const useSharedFilterStore = create<SharedFilterState>((set, get) => ({
  titleContains: '',
  isIncludedInOptimization: undefined,
  isRuleManaged: undefined,

  sortField: undefined,
  sortOrder: undefined,

  portfolioId: undefined,
  trackPortfolioChange: (id) => {
    const current = get().portfolioId;
    const shouldReset = current !== undefined && current !== id;
    set({ portfolioId: id });
    return shouldReset;
  },

  pageSize: DEFAULT_PAGE_SIZE,
  setPageSize: (pageSize) => set({ pageSize }),

  handleChangeTitleContains: (title) => set({ titleContains: title }),
  handleSetIsIncludedInOptimization: (value) => set({ isIncludedInOptimization: value }),
  handleSetIsRuleManaged: (value) => set({ isRuleManaged: value }),
  handleChangeSortField: (field) => set({ sortField: field }),
  handleChangeSortOrder: (order) => set({ sortOrder: order }),
}));

// ── Combined type (backward compatible) ────────────────────────────
export type ContentFilterState = ProviderFilterState &
  ContentRuleFilterState &
  TaxonomyFilterState &
  ContentLabelFilterState &
  SharedFilterState & {
    resetAllFilters: (
      type?:
        | 'providers'
        | 'adAccounts'
        | 'labelsFilter'
        | 'impactGroups'
        | 'contentRules'
        | 'attributionRules'
        | 'impactGroupRules'
        | 'contentLabels',
    ) => void;
  };

// The call signatures plus the non-React statics, spelled out as one type. The exported binding is a
// `const` of this type rather than a function carrying expando properties: TypeScript's declaration
// emitter renders expando props as `declare namespace X { var prop }`, and that generated `var` trips
// `no-var` when the .d.ts tree is linted.
type ContentFilterStoreHook = {
  (): ContentFilterState;
  <T>(selector: (state: ContentFilterState) => T): T;
  getState: () => ContentFilterState;
  setState: (partial: Partial<ContentFilterState>, replace?: boolean) => void;
};

// One copy for the hook and getState(): production calls it through the hook (Clear all, portfolio
// switch) and the unit tests through getState(), so two copies could drift apart unnoticed.
const resetAllFilters: ContentFilterState['resetAllFilters'] = (type) => {
  if (!type) {
    useProviderFilterStore.getState().resetProviderFilters();
    useContentRuleFilterStore.getState().resetContentRuleFilters();
    useTaxonomyFilterStore.getState().resetTaxonomyFilters();
    useContentLabelFilterStore.getState().resetContentLabelFilters();
    useSharedFilterStore.setState({
      titleContains: '',
      isIncludedInOptimization: undefined,
      isRuleManaged: undefined,
    });
    return;
  }

  switch (type) {
    case 'providers':
      useProviderFilterStore.getState().resetProviderFilters('providers');
      break;
    case 'adAccounts':
      useProviderFilterStore.getState().resetProviderFilters('adAccounts');
      break;
    case 'labelsFilter':
      useTaxonomyFilterStore.getState().resetTaxonomyFilters('labelsFilter');
      break;
    case 'impactGroups':
      useTaxonomyFilterStore.getState().resetTaxonomyFilters('impactGroups');
      break;
    case 'contentRules':
      useContentRuleFilterStore.getState().resetContentRuleFilters('contentRules');
      break;
    case 'attributionRules':
      useContentRuleFilterStore.getState().resetContentRuleFilters('attributionRules');
      break;
    case 'impactGroupRules':
      useTaxonomyFilterStore.getState().resetTaxonomyFilters('impactGroupRules');
      break;
    case 'contentLabels':
      useContentLabelFilterStore.getState().resetContentLabelFilters();
      break;
  }
};

/**
 * Backward-compatible hook that merges all filter slices into a single object. New code should import
 * the focused stores directly (useProviderFilterStore / ContentRule / Taxonomy / ContentLabel / Shared).
 */
function useContentFilterStoreImpl(): ContentFilterState;
function useContentFilterStoreImpl<T>(selector: (state: ContentFilterState) => T): T;
function useContentFilterStoreImpl<T>(selector?: (state: ContentFilterState) => T) {
  const providerState = useProviderFilterStore();
  const contentRuleState = useContentRuleFilterStore();
  const taxonomyState = useTaxonomyFilterStore();
  const contentLabelState = useContentLabelFilterStore();
  const sharedState = useSharedFilterStore();

  const combined: ContentFilterState = {
    ...providerState,
    ...contentRuleState,
    ...taxonomyState,
    ...contentLabelState,
    ...sharedState,
    resetAllFilters,
  };

  if (selector) return selector(combined);
  return combined;
}

// Static getState/setState for test compatibility and non-React usage
useContentFilterStoreImpl.getState = (): ContentFilterState => {
  const providerState = useProviderFilterStore.getState();
  const contentRuleState = useContentRuleFilterStore.getState();
  const taxonomyState = useTaxonomyFilterStore.getState();
  const contentLabelState = useContentLabelFilterStore.getState();
  const sharedState = useSharedFilterStore.getState();

  return {
    ...providerState,
    ...contentRuleState,
    ...taxonomyState,
    ...contentLabelState,
    ...sharedState,
    resetAllFilters,
  };
};

useContentFilterStoreImpl.setState = (partial: Partial<ContentFilterState>, replace?: boolean) => {
  const providerDataKeys = new Set(['providersFilter', 'adAccountsFilter']);
  const contentRuleDataKeys = new Set(['contentRulesFilter', 'attributionRulesFilter']);
  const taxonomyDataKeys = new Set(['labelsFilter', 'impactGroupsFilter', 'impactGroupRulesFilter', 'includeNoLabel']);
  const contentLabelDataKeys = new Set(['contentLabels']);
  const sharedDataKeys = new Set([
    'titleContains',
    'isIncludedInOptimization',
    'isRuleManaged',
    'sortField',
    'sortOrder',
    'portfolioId',
    'pageSize',
  ]);

  const pick = (keys: Set<string>, obj: Record<string, unknown>) => {
    const result: Record<string, unknown> = {};
    for (const k of Object.keys(obj)) {
      if (keys.has(k)) result[k] = obj[k];
    }
    return result;
  };

  const p = partial as Record<string, unknown>;
  const providerPart = pick(providerDataKeys, p);
  const contentRulePart = pick(contentRuleDataKeys, p);
  const taxonomyPart = pick(taxonomyDataKeys, p);
  const contentLabelPart = pick(contentLabelDataKeys, p);
  const sharedPart = pick(sharedDataKeys, p);

  // For replace: merge data from partial with current actions to avoid losing store methods
  if (replace) {
    if (Object.keys(providerPart).length) {
      useProviderFilterStore.setState(
        { ...useProviderFilterStore.getState(), ...providerPart } as ProviderFilterState,
        true,
      );
    }
    if (Object.keys(contentRulePart).length) {
      useContentRuleFilterStore.setState(
        { ...useContentRuleFilterStore.getState(), ...contentRulePart } as ContentRuleFilterState,
        true,
      );
    }
    if (Object.keys(taxonomyPart).length) {
      useTaxonomyFilterStore.setState(
        { ...useTaxonomyFilterStore.getState(), ...taxonomyPart } as TaxonomyFilterState,
        true,
      );
    }
    if (Object.keys(contentLabelPart).length) {
      useContentLabelFilterStore.setState(
        { ...useContentLabelFilterStore.getState(), ...contentLabelPart } as ContentLabelFilterState,
        true,
      );
    }
    if (Object.keys(sharedPart).length) {
      useSharedFilterStore.setState({ ...useSharedFilterStore.getState(), ...sharedPart } as SharedFilterState, true);
    }
  } else {
    if (Object.keys(providerPart).length) useProviderFilterStore.setState(providerPart as Partial<ProviderFilterState>);
    if (Object.keys(contentRulePart).length)
      useContentRuleFilterStore.setState(contentRulePart as Partial<ContentRuleFilterState>);
    if (Object.keys(taxonomyPart).length) useTaxonomyFilterStore.setState(taxonomyPart as Partial<TaxonomyFilterState>);
    if (Object.keys(contentLabelPart).length)
      useContentLabelFilterStore.setState(contentLabelPart as Partial<ContentLabelFilterState>);
    if (Object.keys(sharedPart).length) useSharedFilterStore.setState(sharedPart as Partial<SharedFilterState>);
  }
};

export const useContentFilterStore: ContentFilterStoreHook = useContentFilterStoreImpl;

// Re-export focused stores for direct usage
export { useProviderFilterStore } from './provider-filter';
export { useContentRuleFilterStore } from './content-rule-filter';
export { useTaxonomyFilterStore } from './taxonomy-filter';
export { useContentLabelFilterStore } from './content-label-filter';
export type { ProviderFilterState } from './provider-filter';
export type { ContentRuleFilterState } from './content-rule-filter';
export type { TaxonomyFilterState } from './taxonomy-filter';
export type { ContentLabelFilterState } from './content-label-filter';
